import AdminLayout from "../../layouts/AdminLayout";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { addLeavePolicy, getLeavePolicies } from "../../api/attendanceService";

const MONTHS = [
  { value: 1,  label: "January"   },
  { value: 2,  label: "February"  },
  { value: 3,  label: "March"     },
  { value: 4,  label: "April"     },
  { value: 5,  label: "May"       },
  { value: 6,  label: "June"      },
  { value: 7,  label: "July"      },
  { value: 8,  label: "August"    },
  { value: 9,  label: "September" },
  { value: 10, label: "October"   },
  { value: 11, label: "November"  },
  { value: 12, label: "December"  },
];

const currentYear  = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 7 }, (_, i) => currentYear - 3 + i);

function AddLeavePolicy() {
  const [year,             setYear]             = useState(String(currentYear));
  const [months,           setMonths]           = useState([]);
  const [casualLeaveLimit, setCasualLeaveLimit] = useState("");
  const [loading,          setLoading]          = useState(false);
  const [existingPolicies, setExistingPolicies] = useState([]);
  const [successMsg,       setSuccessMsg]       = useState("");
  const [errorMsg,         setErrorMsg]         = useState("");

  /* ── Load existing policies ── */
  const fetchPolicies = async () => {
    try {
      const res = await getLeavePolicies();
      setExistingPolicies(res.data.leave_policies || []);
    } catch (err) {
      console.error("Failed to load policies", err);
    }
  };

  useEffect(() => { fetchPolicies(); }, []);

  const existingForYear = existingPolicies.filter(
    (p) => String(p.year) === String(year)
  );

  const existingMonthMap = {};
  existingForYear.forEach((p) => { existingMonthMap[p.month] = p.casual_leave_limit; });

  /* ── Month toggle ── */
  const handleMonthToggle = (monthVal) => {
    setMonths((prev) =>
      prev.includes(monthVal) ? prev.filter((m) => m !== monthVal) : [...prev, monthVal]
    );
  };

  const allSelected    = months.length === 12;
  const handleSelectAll = () =>
    setMonths(allSelected ? [] : MONTHS.map((m) => m.value));

  /* ── Submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");

    if (!year)                                        { setErrorMsg("Please select a year.");         return; }
    if (months.length === 0)                          { setErrorMsg("Please select at least one month."); return; }
    if (!casualLeaveLimit || parseFloat(casualLeaveLimit) <= 0) {
      setErrorMsg("Please enter a valid leave limit."); return;
    }

    try {
      setLoading(true);
      const payload = {
        year:               Number(year),
        months:             months.map(Number),
        casual_leave_limit: parseFloat(casualLeaveLimit),
      };
      const res = await addLeavePolicy(payload);
      setSuccessMsg(res.data.message || "Leave policy saved successfully ✅");
      setMonths([]);
      setCasualLeaveLimit("");
      await fetchPolicies();
    } catch (error) {
      console.error(error.response?.data);
      setErrorMsg(error.response?.data?.error || "Failed to save leave policy");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        <div className="w-full max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6 items-start">

            {/* ── FORM CARD ── */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-white/15 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-5 sm:p-8"
            >
              {/* Header */}
              <div className="flex items-center gap-3 sm:gap-4 mb-6">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg flex-shrink-0">
                  <span className="text-2xl sm:text-3xl">🏖️</span>
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-white">Add Leave Policy</h1>
                  <p className="text-white/60 text-xs sm:text-sm">Configure casual leave limits per month</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">

                {/* Year */}
                <div>
                  <label className="block text-white font-semibold mb-2 text-sm">Year</label>
                  <select
                    value={year}
                    onChange={(e) => { setYear(e.target.value); setMonths([]); setSuccessMsg(""); setErrorMsg(""); }}
                    required
                    className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white text-sm outline-none"
                  >
                    {YEAR_OPTIONS.map((y) => (
                      <option key={y} value={y} className="bg-gray-800 text-white">
                        {y} {y === currentYear ? "(Current)" : ""}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Months */}
                <div>
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <label className="text-white font-semibold text-sm">
                      Select Months
                      {months.length > 0 && (
                        <span className="ml-2 text-purple-300 text-xs font-normal">
                          ({months.length} selected)
                        </span>
                      )}
                    </label>
                    <button
                      type="button"
                      onClick={handleSelectAll}
                      className="text-xs text-purple-300 hover:text-purple-200 underline"
                    >
                      {allSelected ? "Deselect All" : "Select All"}
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {MONTHS.map((m) => {
                      const isChecked    = months.includes(m.value);
                      const existingLimit = existingMonthMap[m.value];
                      const hasPolicy    = existingLimit !== undefined;

                      return (
                        <label
                          key={m.value}
                          className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border cursor-pointer transition-all ${
                            isChecked
                              ? "bg-purple-500/20 border-purple-400/50"
                              : "bg-white/5 border-white/15 hover:bg-white/10"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleMonthToggle(m.value)}
                            className="w-4 h-4 accent-purple-400 flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <span className="text-white text-sm font-medium">{m.label}</span>
                            {hasPolicy && (
                              <span className="block text-xs text-yellow-300/70 mt-0.5">
                                Existing: {existingLimit} days
                              </span>
                            )}
                          </div>
                          {isChecked && hasPolicy && (
                            <span className="text-yellow-400 text-xs flex-shrink-0">↻</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Leave limit */}
                <div>
                  <label className="block text-white font-semibold mb-2 text-sm">
                    Casual Leave Limit (Per Month)
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0.5"
                    max="31"
                    value={casualLeaveLimit}
                    onChange={(e) => setCasualLeaveLimit(e.target.value)}
                    required
                    placeholder="e.g. 1.5"
                    className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white text-sm placeholder-white/40 outline-none"
                  />
                  <p className="text-white/40 text-xs mt-1">
                    This limit will be applied to all selected months
                  </p>
                </div>

                {/* Messages */}
                {successMsg && (
                  <div className="bg-emerald-500/20 border border-emerald-400/40 rounded-xl px-4 py-3">
                    <p className="text-emerald-300 text-sm font-medium">✅ {successMsg}</p>
                  </div>
                )}
                {errorMsg && (
                  <div className="bg-red-500/20 border border-red-400/40 rounded-xl px-4 py-3">
                    <p className="text-red-300 text-sm font-medium">⚠️ {errorMsg}</p>
                  </div>
                )}

                {/* Submit */}
                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    disabled={loading || months.length === 0}
                    className="w-full sm:w-auto px-8 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500
                      hover:from-purple-600 hover:to-pink-600 text-white font-bold text-sm shadow-lg
                      hover:scale-105 active:scale-95 transition-all
                      disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {loading ? "Saving..." : "Save Leave Policy"}
                  </button>
                </div>
              </form>
            </motion.div>

            {/* ── EXISTING POLICIES CARD ── */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.15 }}
              className="bg-white/15 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-5 sm:p-8"
            >
              <h3 className="text-lg sm:text-xl font-bold text-white mb-1">
                Existing Policies — {year}
              </h3>
              <p className="text-white/50 text-xs mb-5">
                Months already configured for the selected year. Selecting them again will update the limit.
              </p>

              {existingForYear.length === 0 ? (
                <div className="text-center py-10 text-white/30 text-sm">
                  No policies configured for {year} yet.
                </div>
              ) : (
                <div className="space-y-2">
                  {MONTHS.filter((m) => existingMonthMap[m.value] !== undefined).map((m) => (
                    <div
                      key={m.value}
                      className="flex items-center justify-between px-4 py-3 rounded-xl bg-white/5 border border-white/10 gap-3"
                    >
                      <span className="text-white text-sm font-medium">{m.label}</span>
                      <span className="px-3 py-1 rounded-lg bg-purple-500/30 text-purple-200 border border-purple-400/30 text-xs font-semibold flex-shrink-0">
                        {existingMonthMap[m.value]} days / month
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 flex gap-3 bg-blue-500/10 border border-blue-400/20 rounded-xl p-4">
                <span className="text-xl flex-shrink-0">ℹ️</span>
                <div className="text-white/60 text-xs space-y-1">
                  <p>• Policies shown with <span className="text-yellow-300">↻</span> will be updated on save</p>
                  <p>• Decimal values allowed (e.g. 1.5 days)</p>
                  <p>• Leave balance is calculated cumulatively across the financial year</p>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AddLeavePolicy;